import { WorkflowWorkflowInfo } from '@calm/evexia-web-sdk';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import { useMediaPackProgram } from '@rhg/hooks/use-media-pack-program';
import { useProgram } from '@rhg/hooks/use-program';
import { useProgramContext } from '@rhg/hooks/use-program-context';
import {
    ProgramContextState,
    ProgramContextProps,
} from '@rhg/hooks/use-program-context/types';
import { useWorkflowByDefnIdForRecipient } from '@rhg/hooks/use-workflows';
import { createContext, PropsWithChildren } from 'react';

export const ProgramContext = createContext<ProgramContextState>({
    isFetched: false,
    numSessions: 0,
    toolsByGuide: {},
});

export function ProgramProvider({
    program,
    programMetadata,
    packClass,
    workflow,
    packProgram,
    contentFeedbackId,
    recommendation,
    isFetched,
    children,
}: PropsWithChildren<ProgramContextProps>) {
    const state = useProgramContext({
        program,
        programMetadata,
        packClass,
        workflow,
        packProgram,
        recommendation,
        contentFeedbackId,
        isFetched,
    });
    return (
        <ProgramContext.Provider value={state}>
            {children}
        </ProgramContext.Provider>
    );
}

export function ActiveProgramProvider({
    programId,
    packClass,
    workflow,
    children,
}: PropsWithChildren<{
    programId?: string;
    packClass?: string;
    workflow?: WorkflowWorkflowInfo;
}>) {
    const { activeCircleId } = useActiveCircle();
    const { program: activeProgram, isFetched: isProgramFetched } = useProgram(
        programId,
        activeCircleId
    );
    const { workflow: activeWorkflow, isFetched: isWorkflowFetched } =
        useWorkflowByDefnIdForRecipient({
            workflowDefnId: workflow?.workflowDefn.defnId ?? '',
            circleId: activeCircleId,
        });

    const { packProgram, isFetched: isPackProgramFetched } =
        useMediaPackProgram({
            packClass,
            programId,
        });

    return (
        <ProgramProvider
            program={activeProgram}
            workflow={activeWorkflow}
            packClass={packClass}
            packProgram={packProgram}
            isFetched={
                isWorkflowFetched && isProgramFetched && isPackProgramFetched
            }
        >
            {children}
        </ProgramProvider>
    );
}
